// The players page's Export… dialog (spec/dashboard.md section 11.1; spec/api-conventions.md section 21.7): since (players
// seen at or after an instant; empty = every player) and the collections (empty = every collection) → POST
// …/envs/{env}/players:export → the players.export job followed with JobProgress, then the download link.
import { useState } from 'react';
import { ops } from '../../api/operations';
import type { Job } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ExportJob } from '../../content/exports';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { DateTimeInput } from '../../ui/values';
import { secondsToIso } from '../settings/keys';

export function PlayersExportDialog({ env, onClose }: { env: string; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, offsets, can } = useGame();
  const toasts = useToasts();
  const [since, setSince] = useState<number | null>(null);
  const [collections, setCollections] = useState('');
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const permission = can('players_read', env) ? null : 'players_read';
  const list = collections
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);
  const submit = async () => {
    setBusy(true);
    try {
      const queued = await client.data(ops.exportPlayers, { path: { org, game: game.slug, env }, body: { since: secondsToIso(since), collections: list.length ? list : null }, idempotencyKey });
      setJob(queued as Job);
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Export players · ${env}`}
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>{job ? 'Close' : 'Cancel'}</Button>
          {!job && (
            <Button variant="primary" onClick={() => void submit()} busy={busy} missingPermission={permission}>
              Export
            </Button>
          )}
        </>
      }
    >
      <div className="stack" data-testid="players-export-dialog">
        <p className="muted small">Every player of the environment with its bindings and the current value of every save key, one JSON object per line (gzip NDJSON), no row cap; the file is kept 7 days.</p>
        {job ? (
          <ExportJob job={job} filename={`players-${env}.ndjson.gz`} permission={permission} />
        ) : (
          <>
            <Field label="Seen since" help="Only the players seen at or after this instant; empty exports every player.">
              <DateTimeInput aria-label="Seen since" allowEmpty ctx={{ offsets }} value={since} onCommit={setSince} />
            </Field>
            <Field label="Collections" help="Comma- or space-separated collection names; empty exports every collection.">
              <TextInput aria-label="Collections" value={collections} onChange={(e) => setCollections(e.target.value)} placeholder="every collection" />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}
